import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import { AlertTriangle } from "lucide-react";
import { acknowledgeSilentFailure, listSilentFailures } from "@/lib/silent-failures.functions";
import { buildSilentFailureFixPrompt } from "@/lib/fix-prompt.functions";
import { FixPromptButton } from "@/components/admin/FixPromptButton";
import { silentFailureAreaLabel, type SilentFailureRow } from "@/lib/silent-failures";

export const Route = createFileRoute("/_admin/admin/silent-failures")({
  // Der Arbeitsvorrat springt mit `status=offen` genau in die offene Liste.
  validateSearch: (search: Record<string, unknown>) => ({
    status: search['status'] === "alle" ? ("alle" as const) : ("offen" as const),
  }),
  component: SilentFailuresPage,
});

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString("de-DE", { dateStyle: "medium", timeStyle: "short" });
}

function SilentFailuresPage() {
  const fetchRows = useServerFn(listSilentFailures);
  const ack = useServerFn(acknowledgeSilentFailure);
  const fixPrompt = useServerFn(buildSilentFailureFixPrompt);
  const { status } = Route.useSearch();
  const [open, setOpen] = useState(status !== "alle");
  const [busyId, setBusyId] = useState<string | null>(null);
  const [detailId, setDetailId] = useState<string | null>(null);

  const { data, isLoading, error, refetch, isFetching } = useQuery<{ rows: SilentFailureRow[] }>({
    queryKey: ["silent-failures", open],
    queryFn: () => fetchRows({ data: { open, limit: 200 } }),
  });

  const rows: SilentFailureRow[] = data?.rows ?? [];

  async function onAcknowledge(id: string) {
    setBusyId(id);
    try {
      await ack({ data: { id } });
      await refetch();
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className="mx-auto max-w-7xl">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-extrabold text-primary">Stille Störungen</h1>
          <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
            Vorgänge, die fehlgeschlagen sind, ohne den Ablauf zu stoppen: eine Herkunft, die
            nicht entstanden ist, eine Benachrichtigung ohne Empfänger, ein Protokoll, das nicht
            geschrieben wurde. Jede Zeile bleibt stehen, bis sie quittiert wird.
          </p>
        </div>
        <AlertTriangle className="h-8 w-8 shrink-0 text-primary" aria-hidden />
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-2">
        {[
          { value: true, label: "Offen" },
          { value: false, label: "Alle" },
        ].map((opt) => (
          <button
            key={String(opt.value)}
            onClick={() => setOpen(opt.value)}
            className={
              "rounded-md border px-3 py-1.5 text-xs font-semibold transition-colors " +
              (open === opt.value
                ? "border-primary bg-primary text-primary-foreground"
                : "border-border text-muted-foreground hover:bg-secondary")
            }
          >
            {opt.label}
          </button>
        ))}
        <button
          onClick={() => refetch()}
          className="rounded-md border border-border px-3 py-1.5 text-xs font-semibold text-muted-foreground hover:bg-secondary"
        >
          {isFetching ? "Lädt …" : "Aktualisieren"}
        </button>
      </div>

      {error ? (
        <p className="mt-6 text-sm text-destructive">
          Die Liste konnte nicht geladen werden. Bitte erneut versuchen.
        </p>
      ) : isLoading ? (
        <p className="mt-6 text-sm text-muted-foreground">Lädt …</p>
      ) : rows.length === 0 ? (
        <p className="mt-6 rounded-md border border-border bg-secondary/40 p-4 text-sm text-muted-foreground">
          Keine offenen Störungen.
        </p>
      ) : (
        <div className="mt-6 overflow-x-auto rounded-md border border-border">
          <table className="w-full min-w-[900px] text-sm">
            <thead className="bg-secondary/60 text-left text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-3 py-2.5">Zeitpunkt</th>
                <th className="px-3 py-2.5">Bereich</th>
                <th className="px-3 py-2.5">Vorgang</th>
                <th className="px-3 py-2.5">Beschreibung</th>
                <th className="px-3 py-2.5">Bezug</th>
                <th className="px-3 py-2.5" />
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id} className="border-t border-border align-top">
                  <td className="whitespace-nowrap px-3 py-2.5 text-xs text-muted-foreground">
                    {formatDate(row.occurred_at)}
                  </td>
                  <td className="px-3 py-2.5 text-xs font-semibold text-primary">
                    {silentFailureAreaLabel(row.area)}
                  </td>
                  <td className="px-3 py-2.5 font-mono text-xs">{row.kind}</td>
                  <td className="px-3 py-2.5">
                    {row.summary}
                    {detailId === row.id ? (
                      <pre className="mt-2 max-w-xl overflow-x-auto rounded bg-secondary p-2 text-xs">
                        {row.detailJson}
                      </pre>
                    ) : null}
                    <button
                      onClick={() => setDetailId(detailId === row.id ? null : row.id)}
                      className="mt-1 block text-xs font-semibold text-primary underline"
                    >
                      {detailId === row.id ? "Details ausblenden" : "Details"}
                    </button>
                  </td>
                  <td className="px-3 py-2.5 font-mono text-xs text-muted-foreground">
                    {row.subject ?? "—"}
                  </td>
                  <td className="px-3 py-2.5 text-right">
                    <div className="flex flex-wrap items-center justify-end gap-2">
                      <FixPromptButton load={() => fixPrompt({ data: { id: row.id } })} />
                      {row.acknowledged_at ? (
                        <span className="text-xs text-muted-foreground">
                          quittiert {formatDate(row.acknowledged_at)}
                        </span>
                      ) : (
                        <button
                          disabled={busyId === row.id}
                          onClick={() => onAcknowledge(row.id)}
                          className="rounded-md bg-brand-button px-3 py-1.5 text-xs font-semibold text-brand-button-foreground disabled:opacity-60"
                        >
                          {busyId === row.id ? "…" : "Quittieren"}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
